import type { ChatwootConversation } from './types'

// A selectable company filter option: the company id (the value the backend
// scopes on) plus a human label combining the client number and name. `label`
// is null when the company couldn't be resolved to a name/number; the UI shows a
// localized placeholder for those so a raw company id is never displayed.
export interface CompanyOption {
  id: string
  label: string | null
  // Whether the company is archived. Kept as data rather than baked into
  // `label`, because the archived marker is localized and this module is pure —
  // render sites apply it via formatCompanyPickerLabel (ENG-4920).
  archived?: boolean
}

// Build the company filter label: zero-padded client number + name, so an
// operator can search the dropdown by either (e.g. "023" or "Acme GmbH"). Client
// numbers are padded to three digits to match how they are shown elsewhere in
// Operations. Returns null when neither is known — we never fall back to the
// opaque company id as a label.
export function formatCompanyLabel(company: {
  name?: string
  clientNumber?: number | null
}): string | null {
  const number =
    company.clientNumber != null
      ? company.clientNumber.toString().padStart(3, '0')
      : ''
  const name = company.name?.trim() || ''
  return [number, name].filter(Boolean).join(' · ') || null
}

// Apply the archived marker to an already-resolved option label, falling back to
// the unknown placeholder rather than the opaque company id. This is the single
// place the marker is spelled, so a company reads identically wherever it is
// listed — whether the option came from the directory search or from the
// companies seen in the loaded conversations (ENG-4920). Localized strings are
// passed in so this module stays pure.
export function markArchivedLabel(
  option: { label: string | null; archived?: boolean },
  labels: { unknown: string; archived: string }
): string {
  const base = option.label ?? labels.unknown
  return option.archived ? `${base} (${labels.archived})` : base
}

// Label for a company-directory row in the pin/assignment pickers. Archived
// companies are listed there so a conversation can still be filed under a
// company that was archived after the fact (ENG-4920); the suffix is what keeps
// them distinguishable from the active company of the same name.
export function formatCompanyPickerLabel(
  company: { name?: string; clientNumber?: number | null; archived?: boolean },
  labels: { unknown: string; archived: string }
): string {
  return markArchivedLabel(
    { label: formatCompanyLabel(company), archived: company.archived },
    labels
  )
}

// Sort resolved companies alphabetically by label; unresolved (null-label) ones
// fall to the end with a stable id ordering.
export function compareCompanyOptions(
  a: CompanyOption,
  b: CompanyOption
): number {
  if (a.label && b.label) {
    return a.label.localeCompare(b.label)
  }
  if (a.label) {
    return -1
  }
  if (b.label) {
    return 1
  }
  return a.id.localeCompare(b.id)
}

// Distinct companies present in the loaded conversations, keyed by id and sorted
// by label for a stable dropdown. Conversations without a cleverlohn_company_id
// contribute no option (they are never attributed to a company). (REQ-06)
export function distinctCompanies(
  conversations: ChatwootConversation[]
): CompanyOption[] {
  const byId = new Map<string, CompanyOption>()
  for (const conversation of conversations) {
    const company = conversation.company
    const id = company?.id
    if (!id) {
      continue
    }
    const label = formatCompanyLabel(company)
    const existing = byId.get(id)
    // A record with a resolved label came from an enriched conversation, so its
    // `archived` is trustworthy; one with a null label was never matched to a
    // company doc and carries no flag worth reading.
    const resolved = label !== null
    // Tracked independently of the label: whether the flag is current has
    // nothing to do with whether the label still needs upgrading, so a later
    // enriched record must be able to flip it either way (ENG-4920). Re-setting
    // an existing key leaves Map insertion order — and so the sort — untouched.
    byId.set(id, {
      id,
      // Keep the first usable label; upgrade an unresolved (null) placeholder
      // once a real name/number arrives on a later page.
      label: existing?.label ?? label,
      archived: resolved
        ? company.archived === true
        : (existing?.archived ?? false),
    })
  }
  return [...byId.values()].sort(compareCompanyOptions)
}

// A selectable customer (the contact who wrote in): the Chatwoot contact id plus
// a human-readable label (name, falling back to email, then id).
export interface ContactOption {
  id: string
  label: string
}

// Distinct customers present in the loaded conversations, keyed by contact id
// and sorted by label for a stable dropdown. Conversations whose contact has no
// id contribute no option (we can't filter the backend by them).
export function distinctContacts(
  conversations: ChatwootConversation[]
): ContactOption[] {
  const byId = new Map<string, ContactOption>()
  for (const conversation of conversations) {
    const contact = conversation.primary_contact
    const id = contact?.id
    if (!id) {
      continue
    }
    // Keep the first non-empty label we see; a later page may carry a blanker
    // sender for the same contact.
    const label = contact.name || contact.email || id
    const existing = byId.get(id)
    if (!existing || (existing.label === id && label !== id)) {
      byId.set(id, { id, label })
    }
  }
  return [...byId.values()].sort((a, b) => a.label.localeCompare(b.label))
}
